import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDonation } from '../../contexts/DonationContext';
import { saveDonationToFirestore } from '../../services/donationService';
import PaymentSuccessModal from '../../components/PaymentSuccessModal/PaymentSuccessModal';
import PaymentGatewaySelector from '../../components/PaymentGatewaySelector/PaymentGatewaySelector';
import RazorpayPayment from '../../components/RazorpayPayment/RazorpayPayment';
import PayPalPayment from '../../components/PayPalPayment/PayPalPayment';
import './DonateAnonymous.css';

const DonateAnonymous: React.FC = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { selectedCause } = useDonation();

    // Get initial cause from various sources
    const getInitialCause = () => {
        const stateCause = (location.state as any)?.cause;
        const params = new URLSearchParams(window.location.search);
        const urlCause = params.get('cause');
        return stateCause || urlCause || selectedCause || null;
    };

    const initialCause = getInitialCause();
    const defaultProject = t('donationForm.projects.childEducation');

    // State
    const [selectedAmount, setSelectedAmount] = useState('25');
    const [selectedProject, setSelectedProject] = useState(initialCause || defaultProject);
    const [customProject, setCustomProject] = useState('');
    const [isCustomProject, setIsCustomProject] = useState(false);
    const [donationFrequency] = useState<'one-time'>('one-time');
    const [customAmount, setCustomAmount] = useState('');
    const [paymentGateway, setPaymentGateway] = useState<'razorpay' | 'paypal'>('razorpay');
    const [currentStep, setCurrentStep] = useState(1);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [showPaymentSuccessModal, setShowPaymentSuccessModal] = useState(false);
    const [paymentSuccessData, setPaymentSuccessData] = useState<any>(null);

    // Project options
    const projectOptions = [
        t('donationForm.projects.childEducation'),
        t('donationForm.projects.cleanWater'),
        t('donationForm.projects.shelter'),
        t('donationForm.projects.healthcare'),
        t('donationForm.projects.general'),
        t('donationForm.projects.custom')
    ];

    // Get donation amount
    const getDonationAmount = (): number => {
        if (selectedAmount === 'others') {
            return parseFloat(customAmount) || 0;
        }
        const amounts: Record<string, number> = {
            '10': 500,
            '25': 1000,
            '50': 2500,
            '75': 5000,
            '100': 10000
        };
        return amounts[selectedAmount] || 0;
    };

    // Handle project change
    const handleProjectChange = (value: string) => {
        if (value === t('donationForm.projects.custom')) {
            setIsCustomProject(true);
            setSelectedProject('');
        } else {
            setIsCustomProject(false);
            setSelectedProject(value);
            setCustomProject('');
        }
        setErrors({ ...errors, project: '' });
    };

    // Validate step 1
    const validateStep1 = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!initialCause) {
            if (isCustomProject && !customProject.trim()) {
                newErrors.project = t('donationForm.errors.projectRequired');
            } else if (!isCustomProject && !selectedProject) {
                newErrors.project = t('donationForm.errors.projectRequired');
            }
        }
        if (selectedAmount === 'others' && !customAmount) {
            newErrors.donationAmount = t('donationForm.errors.amountRequired');
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Navigation handlers
    const handleNext = () => {
        if (validateStep1()) {
            setCurrentStep(2);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleBack = () => {
        setCurrentStep(1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Payment success handler
    const handlePaymentSuccess = async (paymentData: any) => {
        const project = selectedProject || customProject || initialCause || 'General Donation';

        const donationData = {
            donationType: 'anonymous' as const,
            donationFrequency,
            project,
            amount: getDonationAmount(),
            userId: null,
            firstName: 'Anonymous',
            lastName: 'Donor',
            email: '',
            phone: '',
            paymentStatus: 'success',
            paymentMethod: paymentGateway,
            ...(paymentGateway === 'razorpay' ? {
                razorpay_payment_id: paymentData.razorpay_payment_id,
                razorpay_order_id: paymentData.razorpay_order_id,
                razorpay_signature: paymentData.razorpay_signature
            } : {
                paypal_order_id: paymentData.paypal_order_id,
                paypal_payer_id: paymentData.paypal_payer_id,
                amountUSD: paymentData.amountUSD
            })
        };

        const transactionId = paymentData.razorpay_payment_id || paymentData.paypal_order_id || `TXN${Date.now()}`;
        const donationId = `DON${Date.now()}`;

        const donationToSave = {
            ...donationData,
            transactionId,
            donationId
        };

        await saveDonationToFirestore(donationToSave);

        setPaymentSuccessData({
            amount: donationData.amount,
            project: donationData.project,
            donationFrequency: donationData.donationFrequency,
            paymentMethod: donationData.paymentMethod,
            paymentStatus: 'success',
            transactionId,
            firstName: 'Anonymous',
            lastName: 'Donor',
            createdAt: new Date().toISOString(),
            donationId
        });
        setShowPaymentSuccessModal(true);
    };

    // Payment failure handler
    const handlePaymentFailure = (error: any) => {
        console.error('Payment failed:', error);
        setErrors({ payment: error.message || 'Payment failed. Please try again.' });
    };

    return (
        <div className="donate-page">
            <section className="donate-section">
                <div className="donate-content">
                    {/* Left Section - Image */}
                    <div className="donate-image-section">
                        <div className="donate-image-container">
                            <img
                                src="/images/donate/donate.jpg"
                                alt="Donation"
                                className="donate-image"
                            />
                            <div className="donate-image-overlay">
                                <div className="donate-welcome-text">
                                    <div className="welcome-line">Donate Anonymously</div>
                                    <div className="welcome-title">Quick & Private</div>
                                    <div className="welcome-subtitle">Make a difference without creating an account.</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Section - Form */}
                    <div className="donate-form-section">
                        <div className="donate-form-card">
                            <div className="form-card-label">ANONYMOUS DONATION</div>
                            <h1 className="form-card-title">Donate Anonymously</h1>
                            <p className="form-card-subtitle">Quick checkout - No account needed</p>

                            {/* Progress Steps */}
                            <div className="donate-progress">
                                <div className={`progress-step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
                                    <div className="step-number">1</div>
                                    <div className="step-label">{t('donate.steps.details')}</div>
                                </div>
                                <div className={`progress-step ${currentStep >= 2 ? 'active' : ''}`}>
                                    <div className="step-number">2</div>
                                    <div className="step-label">{t('donate.steps.payment')}</div>
                                </div>
                            </div>

                            {/* Error Display */}
                            {errors.payment && (
                                <div className="error-banner">
                                    {errors.payment}
                                </div>
                            )}

                            <form className="donate-form">
                                {/* Step 1: Donation Details */}
                                {currentStep === 1 && (
                                    <div className="form-step">
                                        {/* Project Selection */}
                                        <div className="form-group">
                                            <label>
                                                {t('donationForm.project')}
                                                <span className="required-asterisk">*</span>
                                            </label>
                                            {initialCause ? (
                                                <input type="text" value={initialCause} readOnly className="readonly-input" />
                                            ) : (
                                                <>
                                                    <select
                                                        className={`project-select ${errors.project ? 'error' : ''}`}
                                                        value={isCustomProject ? t('donationForm.projects.custom') : selectedProject}
                                                        onChange={(e) => handleProjectChange(e.target.value)}
                                                    >
                                                        <option value="">{t('donationForm.selectProject')}</option>
                                                        {projectOptions.map((option, index) => (
                                                            <option key={index} value={option}>{option}</option>
                                                        ))}
                                                    </select>
                                                    {isCustomProject && (
                                                        <input
                                                            type="text"
                                                            placeholder={t('donationForm.customProjectPlaceholder')}
                                                            value={customProject}
                                                            onChange={(e) => {
                                                                setCustomProject(e.target.value);
                                                                setErrors({ ...errors, project: '' });
                                                            }}
                                                            className={`custom-project-input ${errors.project ? 'error' : ''}`}
                                                        />
                                                    )}
                                                    {errors.project && <span className="error-message">{errors.project}</span>}
                                                </>
                                            )}
                                        </div>

                                        {/* Amount Selection */}
                                        <div className="form-group">
                                            <label>
                                                {t('donationForm.yourDonation')}
                                                <span className="required-asterisk">*</span>
                                            </label>
                                            <div className="donation-amounts">
                                                {['10', '25', '50', '75', '100', 'others'].map((amount) => (
                                                    <button
                                                        key={amount}
                                                        type="button"
                                                        className={`amount-button ${selectedAmount === amount ? 'active' : ''}`}
                                                        onClick={() => {
                                                            setSelectedAmount(amount);
                                                            if (amount !== 'others') setCustomAmount('');
                                                        }}
                                                    >
                                                        {t(`donationForm.amounts.${amount}`)}
                                                    </button>
                                                ))}
                                            </div>
                                            {selectedAmount === 'others' && (
                                                <input
                                                    type="number"
                                                    placeholder={t('donationForm.customAmountPlaceholder')}
                                                    value={customAmount}
                                                    onChange={(e) => setCustomAmount(e.target.value)}
                                                    className={`custom-amount-input ${errors.donationAmount ? 'error' : ''}`}
                                                    min="1"
                                                />
                                            )}
                                            {errors.donationAmount && <span className="error-message">{errors.donationAmount}</span>}
                                        </div>

                                        <div className="form-actions">
                                            <button type="button" className="btn-next" onClick={handleNext}>
                                                {t('donate.next')} →
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Step 2: Payment Gateway */}
                                {currentStep === 2 && (
                                    <div className="form-step">
                                        <h2 className="step-title">{t('donate.steps.payment')}</h2>

                                        <PaymentGatewaySelector
                                            selectedGateway={paymentGateway}
                                            onSelectGateway={setPaymentGateway}
                                            amount={getDonationAmount()}
                                        />

                                        {paymentGateway === 'razorpay' ? (
                                            <RazorpayPayment
                                                amount={getDonationAmount()}
                                                project={selectedProject || customProject || initialCause || 'Donation'}
                                                donationFrequency={donationFrequency}
                                                onSuccess={handlePaymentSuccess}
                                                onFailure={handlePaymentFailure}
                                            />
                                        ) : (
                                            <PayPalPayment
                                                amount={getDonationAmount()}
                                                project={selectedProject || customProject || initialCause || 'Donation'}
                                                donationFrequency={donationFrequency}
                                                onSuccess={handlePaymentSuccess}
                                                onFailure={handlePaymentFailure}
                                            />
                                        )}

                                        <div className="form-actions">
                                            <button type="button" className="btn-back" onClick={handleBack}>
                                                ← {t('donate.back')}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            {/* Success Modal */}
            {paymentSuccessData && (
                <PaymentSuccessModal
                    isOpen={showPaymentSuccessModal}
                    onClose={() => {
                        setShowPaymentSuccessModal(false);
                        navigate('/');
                    }}
                    donationData={paymentSuccessData}
                />
            )}
        </div>
    );
};

export default DonateAnonymous;

